import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../Css/Registration.css';
import '../Css/PlanSelectModal.css';
import { useNavigate, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Button, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import 'react-toastify/dist/ReactToastify.css';
import { CircularProgress } from '@mui/material';
import Logger from './Logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const Registration = () => {
    const [formData, setFormData] = useState({
        name: '',
        organization: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        gst: '',
        upi: '',
        address: '',
        referralCode: '',
    });

    const [otp, setOtp] = useState('');
    const [plans, setPlans] = useState([]);
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false); // Loading state for sending OTP
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false); // Loading state for verifying OTP
    const navigate = useNavigate();

    // Initialize Logger
    const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

    const validateUPI = (upi) => {
        // Regular expression to match a basic UPI ID format (e.g., user@bank)
        const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{3,64}$/;
        return upiRegex.test(upi);
    };


    useEffect(() => {
        const { name, phone, email, password, confirmPassword, gst, address } = formData;
        setIsFormComplete(name && phone && email && password && confirmPassword && gst && address);
    }, [formData]);

    const handleChange = (e) => {
        // Allow only digits in the phone number field
        if (e.target.name === 'phone') {
            e.target.value = e.target.value.replace(/\D/g, ''); // Remove any non-digit characters
            // Limit the input to 10 digits
            if (e.target.value.length > 10) {
                e.target.value = e.target.value.slice(0, 10);
            }
        }
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const validateGST = (gst) => {
        return /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d[Z]{1}[A-Z\d]{1}$/.test(gst);
    };

    const validateForm = () => {
        const { name, organization, phone, email, password, confirmPassword, gst, address, upi } = formData;

        if (!name) {
            toast.error('Name is required');
            return false;
        }

        if (!organization) {
            toast.error('Organization Name is required');
            return false;
        }

        if (!phone.match(/^\d{10}$/)) {
            toast.error('Phone number must be 10 digits');
            return false;
        }

        if (!email.includes('@')) {
            toast.error('Invalid email address');
            return false;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return false;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return false;
        }

        if (!validateGST(gst)) {
            toast.error('Invalid GST number. GST number should be 15 alphanumeric characters.');
            return false;
        }

        if (!validateUPI(upi)) {
            toast.error('Invalid UPI ID. Please enter a valid UPI ID.');
            return false;
        }

        if (!address) {
            toast.error('Address is required');
            return false;
        }

        return true;
    };

    const handleSendAndStoreOTP = async () => {
        if (!isFormComplete) {
            toast.error('Please fill all the fields');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setIsSendingOtp(true);
        try {
            const response = await fetch(`/api/signup/send-store-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: formData.email,
                    phone: formData.phone,
                    gst: formData.gst,
                    organization: formData.organization
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error sending OTP. Please try again.');
            }

            logger.log(`OTP sent for registration! ${data}`, 'INFO');
            toast.success('OTP sent to your email. Please check your inbox.');
            setIsOtpSent(true);

        } catch (error) {
            logger.log(`Error sending OTP: ${error.message}`, 'ERROR');
            toast.error(error.message || 'Error sending OTP. Please try again.');
        } finally {
            setIsSendingOtp(false); // Stop loading indicator
        }
    };

    const handleVerifyOTP = async () => {
        setIsVerifyingOtp(true);
        try {
            const response = await fetch(`/api/signup/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: formData.email,
                    otp
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Invalid OTP. Please try again.');
            }

            logger.log(`OTP verified for registration ${JSON.stringify(data)}`, 'INFO');
            toast.success('Email verified successfully.');
            setIsEmailVerified(true);

        } catch (error) {
            logger.log(`Error verifying OTP: ${error.message}`, 'ERROR');
            toast.error(error.message || 'Invalid OTP. Please try again.');
        } finally {
            setIsVerifyingOtp(false); // Stop loading indicator
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isEmailVerified) {
            toast.error('Please verify your email before submitting the form.');
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            setOpenConfirmationModal(true);
        } catch (error) {
            logger.log(`Error registering: ${error.response?.data?.message || error.message}`, 'ERROR');
            toast.error(error.response?.data?.message || 'Error registering. Please try again.');
        }
    };

    const handleOptionSelect = async (plan) => {
        setOpenConfirmationModal(false);
        try {
            const payload = { ...formData, plan };

            // First API call: Sign up the organization
            const response = await fetch(`/api/signup/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            if (response.ok) {
                logger.log(`Registration successful! ${response.data}`, 'INFO');
                toast.success('Registration successful!');
                setTimeout(() => navigate('/'), 3000);
                setFormData({
                    name: '',
                    organization: '',
                    phone: '',
                    email: '',
                    password: '',
                    confirmPassword: '',
                    gst: '',
                    upi: '',
                    address: '',
                    referralCode: ''
                });
                setOtp('');
                setIsOtpSent(false);
                setIsEmailVerified(false);
            } else {
                toast.error('Registeration Failed. Please Register again');
                setFormData({
                    name: '',
                    organization: '',
                    phone: '',
                    email: '',
                    password: '',
                    confirmPassword: '',
                    gst: '',
                    upi: '',
                    address: '',
                    referralCode: ''
                });
                setOtp('');
                setIsOtpSent(false);
                setIsEmailVerified(false);
            }

        }
        catch (error) {
            logger.log(`Error during registration: ${error.message}`, 'ERROR');
            toast.error("Some error occured", error);
        }
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const fetchPlans = useCallback(async () => {
        try {
            const response = await fetch(`/api/plan/fetch`);
            if (!response.ok) {
                throw new Error("Unable to fetch plans");
            }
            const data = await response.json();
            if (data === null || data.length === 0) {
                setPlans([]);
            } else {
                setPlans(data);
            }
        }
        catch (error) {
            logger.log(`Error fetching plans: ${error.message}`, 'ERROR');
            setPlans([]);
        }
    }, [logger]);

    useEffect(() => {
        fetchPlans()
    }, [fetchPlans]);

    return (
        <div className='main-registration-container'>
            <div className="registration-container">
                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    pauseOnHover
                    toastClassName={(context) => {
                        const type = context?.type || "default";
                        return `custom-toast custom-toast-${type}`;
                    }}
                />
                <div className="form-wrapper">
                    <h3>Admin Registration</h3>
                    <form className="form" onSubmit={handleSubmit} autocomplete="off">
                        <div className="form-group">
                            <label htmlFor="name" className="label">Name
                                <span class="requiredFields">*</span>
                            </label>
                            <input type="text" name="name" placeholder="Enter your full name" value={formData.name} onChange={handleChange} fullWidth required autocomplete="off" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="organization" className="label">Organization Name
                                <span class="requiredFields">*</span>
                            </label>
                            <input type="text" name="organization" placeholder="Enter your organization name" value={formData.organization} disabled={isSendingOtp || isOtpSent} className={isSendingOtp || isOtpSent ? "input-disabled-register" : ""} onChange={handleChange} fullWidth required autocomplete="off" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone" className="label">Phone
                                <span class="requiredFields">*</span>
                            </label>
                            <input type="text" name="phone" placeholder="Enter your full phone number" value={formData.phone} onChange={handleChange} disabled={isSendingOtp || isOtpSent} className={isSendingOtp || isOtpSent ? "input-disabled-register" : ""} fullWidth required autocomplete="off" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email" className="label">Email
                                <span class="requiredFields">*</span>
                            </label>
                            <div className="email-otp-group">
                                <input type="email" name="email" placeholder="Enter your email" value={formData.email} onChange={handleChange} fullWidth className={isSendingOtp || isOtpSent ? "input-disabled-register" : ""} disabled={isSendingOtp || isOtpSent} required autocomplete="off" />
                                {isSendingOtp && (
                                    <div className="otp-loading">
                                        <CircularProgress size={24} />
                                    </div>
                                )}
                                {!isSendingOtp && (
                                    <button type="button" onClick={handleSendAndStoreOTP} className="OTPbutton" disabled={isSendingOtp || isOtpSent || !isFormComplete} style={{ backgroundColor: "#07a6ef" }}>
                                        Send OTP
                                    </button>
                                )}
                            </div>
                        </div>
                        {isOtpSent && (
                            <div className="form-group">
                                <label htmlFor="otp" className="label">Enter OTP
                                    <span class="requiredFields">*</span>
                                </label>
                                <input type="text" name="otp" placeholder="Enter OTP sent to your email" value={otp} onChange={(e) => setOtp(e.target.value)} fullWidth required autocomplete="off" />
                                {isVerifyingOtp && (
                                    <div className="otp-loading">
                                        <CircularProgress size={24} />
                                    </div>
                                )}
                                {!isVerifyingOtp && (
                                    <>
                                        <br /><br />
                                        <button type="button" onClick={handleVerifyOTP} className="button" disabled={isVerifyingOtp || isEmailVerified}>
                                            Verify OTP
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                        <div className="form-group">
                            <label htmlFor="gst" className="label">GST Number
                                <span class="requiredFields">*</span>
                            </label>
                            <input type="text" name="gst" placeholder="Enter your GST number" value={formData.gst} onChange={handleChange} disabled={isSendingOtp || isOtpSent} className={isSendingOtp || isOtpSent ? "input-disabled-register" : ""} fullWidth required autocomplete="off" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="upi" className="label">UPI Id
                                <span class="requiredFields">*</span>
                            </label>
                            <input type="text" name="upi" placeholder="Enter your UPI Id" value={formData.upi} onChange={handleChange} fullWidth required autocomplete="off" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="address" className="label">Address
                                <span class="requiredFields">*</span>
                            </label>
                            <input type="text" name="address" placeholder="Enter your address" value={formData.address} onChange={handleChange} fullWidth required autocomplete="off" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="password" className="label">Password
                                <span class="requiredFields">*</span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} placeholder="Enter your password" onChange={handleChange} required autocomplete="off" />
                                <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} onClick={togglePasswordVisibility} style={{ marginLeft: -25, cursor: 'pointer' }} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label htmlFor="confirmPassword" className="label">Confirm Password
                                <span class="requiredFields">*</span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} placeholder="Confirm your password" onChange={handleChange} required autocomplete="off" />
                                <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeSlash} onClick={toggleConfirmPasswordVisibility} style={{ marginLeft: -25, cursor: 'pointer' }} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label htmlFor="referralCode" className="label">Referral Code
                            </label>
                            <input type="text" name="referralCode" placeholder="Enter Referral Code(If Any)" value={formData.referralCode} onChange={handleChange} fullWidth autocomplete="off" />
                        </div>
                        <button type="submit" className="button" disabled={!isEmailVerified}>Register</button>
                    </form>
                </div>

                <div className="registration-additional-links">
                    <p>Have an account?
                        <Link to="/"> Sign in</Link></p>
                </div>
                <p className='noteHere'>
                    <span class="requiredFields">*</span> fields are required
                </p>
            </div>
            <Modal
                open={openConfirmationModal}
                onClose={() => setOpenConfirmationModal(false)}
                className="plan-select-overlay"
            >
                <div className="plan-select-modal" role="dialog" aria-labelledby="plan-select-title-reg">
                    <div className="plan-select-header">
                        <IconButton
                            aria-label="Close"
                            onClick={() => setOpenConfirmationModal(false)}
                            size="small"
                            className="plan-select-close"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <h2 id="plan-select-title-reg" className="plan-select-heading">
                            Select a Plan
                        </h2>
                        <p className="plan-select-sub">
                            Choose the plan that fits your team. You can upgrade later from Billing.
                        </p>
                    </div>
                    <div className="plan-select-body">
                        {plans.length > 0 ? (
                            <div className="plan-select-grid">
                                {[...plans]
                                    .filter((p) => {
                                        const n = String(p.planName || "").toLowerCase();
                                        return (
                                            n === "starter" ||
                                            n === "advance" ||
                                            n === "custom" ||
                                            p.isCustom === true
                                        );
                                    })
                                    .sort((a, b) => {
                                        const rank = (p) => {
                                            const n = String(p.planName || "").toLowerCase();
                                            if (n === "starter") return 1;
                                            if (n === "advance") return 2;
                                            if (n === "custom" || p.isCustom) return 3;
                                            return 4;
                                        };
                                        return rank(a) - rank(b);
                                    })
                                    .map((plan) => {
                                        const name = String(plan.planName || "").toLowerCase();
                                        const isCustom = plan.isCustom || name === "custom";
                                        const isStarter =
                                            name === "starter" || plan.charges === "0";
                                        return (
                                            <article
                                                key={plan._id || plan.planNumber}
                                                className="plan-select-card"
                                            >
                                                <div className="plan-select-card-top">
                                                    <h3 className="plan-select-name">
                                                        {plan.planName}
                                                    </h3>
                                                    <div className="plan-select-price">
                                                        {isCustom ? (
                                                            <>
                                                                <span className="plan-select-price-main">
                                                                    Custom
                                                                </span>
                                                                <span className="plan-select-price-sub">
                                                                    Pricing on request
                                                                </span>
                                                            </>
                                                        ) : isStarter ? (
                                                            <>
                                                                <span className="plan-select-price-main">
                                                                    Free
                                                                </span>
                                                                <span className="plan-select-price-sub">
                                                                    Forever
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="plan-select-price-main">
                                                                    ₹{plan.charges}
                                                                </span>
                                                                <span className="plan-select-price-sub">
                                                                    {plan.trialDays > 0
                                                                        ? `/month · ${plan.trialDays}-day free trial`
                                                                        : "/month"}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <dl className="plan-select-features">
                                                    <div className="plan-select-feature">
                                                        <dt>Drivers</dt>
                                                        <dd>
                                                            {plan.allowedDriver === -1
                                                                ? "Unlimited"
                                                                : plan.allowedDriver}
                                                        </dd>
                                                    </div>
                                                    <div className="plan-select-feature">
                                                        <dt>Packers</dt>
                                                        <dd>
                                                            {plan.allowedPacker === -1
                                                                ? "Unlimited"
                                                                : plan.allowedPacker}
                                                        </dd>
                                                    </div>
                                                    <div className="plan-select-feature">
                                                        <dt>Dispatchers</dt>
                                                        <dd>
                                                            {plan.allowedDispatcher === -1
                                                                ? "Unlimited"
                                                                : plan.allowedDispatcher}
                                                        </dd>
                                                    </div>
                                                    <div className="plan-select-feature">
                                                        <dt>Orders / month</dt>
                                                        <dd>
                                                            {plan.freeOrdersPerMonth === -1
                                                                ? "Unlimited"
                                                                : plan.freeOrdersPerMonth}
                                                        </dd>
                                                    </div>
                                                    <div className="plan-select-feature">
                                                        <dt>Live tracking</dt>
                                                        <dd>
                                                            {plan.liveTrackingEnabled
                                                                ? "Yes"
                                                                : "No"}
                                                        </dd>
                                                    </div>
                                                    <div className="plan-select-feature">
                                                        <dt>History</dt>
                                                        <dd>
                                                            {plan.historyDays === -1
                                                                ? "Unlimited"
                                                                : plan.historyDays === 0
                                                                    ? "None"
                                                                    : `${plan.historyDays} days`}
                                                        </dd>
                                                    </div>
                                                    <div className="plan-select-feature">
                                                        <dt>POD</dt>
                                                        <dd>
                                                            {plan.podDays === -1
                                                                ? "Unlimited"
                                                                : plan.podDays === 1
                                                                    ? "Today only"
                                                                    : plan.podDays === 365
                                                                        ? "1 year"
                                                                        : `${plan.podDays ?? 0} days`}
                                                        </dd>
                                                    </div>
                                                </dl>
                                                <Button
                                                    className="plan-select-btn"
                                                    fullWidth
                                                    disableElevation
                                                    onClick={() => handleOptionSelect(plan)}
                                                >
                                                    Select {plan.planName}
                                                </Button>
                                            </article>
                                        );
                                    })}
                            </div>
                        ) : (
                            <p className="plan-select-empty">No plans available</p>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Registration;
